import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, LogOut } from "lucide-react";

/**
 * Phase H.5 — top-of-app banner shown on every authenticated page while a
 * super admin is impersonating another user. Shows the target's identity and
 * a one-click return. For customer targets, adds a "Read-only mode" subtext.
 */
export function ImpersonationBanner() {
    const { user, stopImpersonation } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const imp = user?.impersonating;

    useEffect(() => {
        // Post-reload toast when the interceptor auto-returned us after an
        // expired impersonation JWT.
        if (sessionStorage.getItem("impersonation_expired") === "1") {
            sessionStorage.removeItem("impersonation_expired");
            toast.error("Impersonation session expired — returned to super admin.");
        }
    }, []);

    if (!imp) return null;

    const onReturn = async () => {
        setBusy(true);
        try {
            const landing = await stopImpersonation();
            toast.success(`Returned to super admin (${imp.original_email}).`);
            navigate(landing || "/admin");
        } catch (e) {
            toast.error("Failed to return — please log in again.");
        } finally {
            setBusy(false);
        }
    };

    const displayName = imp.target_email;
    const roleLabel = imp.target_role === "customer" ? "customer" : "admin";
    const isCustomer = imp.target_role === "customer";

    return (
        <div
            data-testid="impersonation-banner"
            className="bg-amber-50 border-b border-amber-300 text-amber-900"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start sm:items-center gap-3 text-sm">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 sm:mt-0" />
                <div className="flex-1 leading-snug">
                    <span>
                        Viewing as <span className="font-semibold" data-testid="imp-banner-target-email">{displayName}</span>{" "}
                        ({roleLabel}).
                    </span>
                    {isCustomer && (
                        <span
                            className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-amber-300"
                            data-testid="imp-banner-readonly-chip"
                        >
                            Read-only mode — writes are disabled
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="imp-banner-return"
                    onClick={onReturn}
                    disabled={busy}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
                >
                    <LogOut size={13} />
                    {busy ? "Returning…" : "Return to super admin"}
                </button>
            </div>
        </div>
    );
}
